import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Firestore操作のベースクラス
 * 共通のCRUD操作とエラーハンドリングを提供
 */
class FirestoreBaseService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  /**
   * リトライ機能付きでFirestore操作を実行
   * @param {Function} operation - 実行する操作
   * @param {number} maxRetries - 最大リトライ回数
   * @param {number} delay - リトライ間の遅延時間（ミリ秒）
   */
  async executeWithRetry(operation, maxRetries = 2, delay = 2000) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        console.error(`Firestore operation failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * ドキュメントを作成または更新
   * @param {string} docId - ドキュメントID
   * @param {Object} data - 保存するデータ
   * @param {boolean} merge - マージモード
   */
  async createOrUpdate(docId, data, merge = false) {
    return this.executeWithRetry(async () => {
      const docRef = doc(this.collectionRef, docId);
      const dataWithTimestamp = {
        ...data,
        updatedAt: serverTimestamp(),
        ...(merge ? {} : { createdAt: serverTimestamp() })
      };
      
      await setDoc(docRef, dataWithTimestamp, { merge });
      return docRef;
    });
  }

  /**
   * 新しいドキュメントを追加（自動ID生成）
   * @param {Object} data - 保存するデータ
   */
  async add(data) {
    return this.executeWithRetry(async () => {
      const dataWithTimestamp = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      return await addDoc(this.collectionRef, dataWithTimestamp);
    });
  }

  /**
   * ドキュメントを取得
   * @param {string} docId - ドキュメントID
   */
  async get(docId) {
    return this.executeWithRetry(async () => {
      const docRef = doc(this.collectionRef, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      
      return null;
    });
  }

  /**
   * ドキュメントを更新
   * @param {string} docId - ドキュメントID
   * @param {Object} data - 更新するデータ
   */
  async update(docId, data) {
    return this.executeWithRetry(async () => {
      const docRef = doc(this.collectionRef, docId);
      const dataWithTimestamp = {
        ...data,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(docRef, dataWithTimestamp);
      return docRef;
    });
  }

  /**
   * ドキュメントを削除
   * @param {string} docId - ドキュメントID
   */
  async delete(docId) {
    return this.executeWithRetry(async () => {
      const docRef = doc(this.collectionRef, docId);
      await deleteDoc(docRef);
    });
  }

  /**
   * 複数ドキュメントを取得
   * @param {Array} conditions - 検索条件の配列 [{field, operator, value}]
   * @param {Array} orderByConditions - ソート条件の配列 [{field, direction}]
   * @param {number} limitCount - 取得件数制限
   */
  async getMany(conditions = [], orderByConditions = [], limitCount = null) {
    return this.executeWithRetry(async () => {
      let q = this.collectionRef;

      // where条件を追加
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });

      // orderBy条件を追加
      orderByConditions.forEach(orderCondition => {
        q = query(q, orderBy(orderCondition.field, orderCondition.direction || 'asc'));
      });

      // limit条件を追加
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    });
  }

  /**
   * 全ドキュメントを取得
   */
  async getAll() {
    return this.getMany();
  }

  /**
   * 条件に一致するドキュメントの存在確認
   * @param {Array} conditions - 検索条件の配列
   */
  async exists(conditions) {
    const docs = await this.getMany(conditions, [], 1);
    return docs.length > 0;
  }

  /**
   * バッチ処理でドキュメントを作成/更新
   * @param {Array} operations - バッチ操作の配列 [{type, docId, data}]
   */
  async batchWrite(operations) {
    return this.executeWithRetry(async () => {
      const batch = writeBatch(db);

      operations.forEach(operation => {
        const docRef = doc(this.collectionRef, operation.docId);
        const dataWithTimestamp = {
          ...operation.data,
          updatedAt: serverTimestamp(),
          ...(operation.type === 'create' ? { createdAt: serverTimestamp() } : {})
        };

        switch (operation.type) {
          case 'create':
          case 'set':
            batch.set(docRef, dataWithTimestamp, { merge: operation.merge || false });
            break;
          case 'update':
            batch.update(docRef, dataWithTimestamp);
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`Unknown operation type: ${operation.type}`);
        }
      });

      await batch.commit();
    });
  }

  /**
   * Firestoreエラーを処理し、ユーザーフレンドリーなメッセージを返す
   * @param {Error} error - Firestoreエラー
   */
  handleError(error) {
    console.error(`Firestore error in ${this.collectionName}:`, error);

    const errorMessages = {
      'permission-denied': 'アクセス権限がありません。',
      'not-found': 'データが見つかりません。',
      'already-exists': 'データが既に存在します。',
      'invalid-argument': '無効な引数が指定されました。',
      'deadline-exceeded': 'リクエストがタイムアウトしました。',
      'unavailable': 'サービスが一時的に利用できません。',
      'unauthenticated': '認証が必要です。'
    };

    const message = errorMessages[error.code] || 'データベースエラーが発生しました。';
    
    return {
      code: error.code,
      message,
      originalError: error
    };
  }
}

export default FirestoreBaseService;